// Layer 3 (Transformations): the Control metallurgist's landing (polish.P26
// #713, locked comp `.impeccable/mocks/decision/landing-control-met.html`,
// variant A finished with C's action read and B's stage-header figure).
// Pure projections over the landing's existing transforms, the Advisory seed's
// recommended actions and the Effectiveness engine — no React, no mutation.

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { Case, RecommendedAction } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { PipelineActor } from '@/versions/v1/modules/advisory/PipelineStrip.transform';
import { casesWithSession } from '@/versions/v1/modules/advisory/Scene.transform';
import { applyRecommendedActions, type AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { UPTAKE_HISTORY_ACTIONS } from '@/versions/v1/shared/bunker-hill-uptake-history-seed';
import { BUNKER_HILL_SEATS } from '@/versions/v1/shared/bunker-hill-org-seed';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import { ISSUE_SPINE } from '@/versions/v1/seed/bunker-hill-issue-spine';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { effectivenessFor, watchedActionForCase } from '@/versions/v1/shared/EffectivenessEngine';
import { formatCaseClock } from '@/versions/v1/shared/clock.derive';
import type { GmMiniItem, LandingStripCell } from './GmLeadBand.transform';
import { issueListRows, MET_LEVEL_ISSUE_IDS, type IssueListRow } from './IssueList.transform';
import { metallurgistKpiRow, type MetKpiTile } from './LandingSeries.transform';
import { firstSentence } from './MetLead.transform';
import { metallurgistCasesKpis, metallurgistLimsKpis, type TabKpiTile } from './RoleLandingTabKpis.transform';
import { stampOf } from './SuptLanding.transform';

export type CmetTabId = 'kpi' | 'lims' | 'cases';

// ---- Header fact -----------------------------------------------------------------

/** The route's one header fact (adjudication 6): the next 12 h composite. */
export function cmetHeaderFact(state: SessionState): { label: string; value: string; caption: string } {
  const tile = metallurgistLimsKpis(state).find((row) => row.id === 'cmet-lims-4')!;
  return { label: tile.label, value: tile.value, caption: tile.caption };
}

// ---- Figure rows -------------------------------------------------------------------

function miniFromMet(tile: MetKpiTile): GmMiniItem {
  return {
    id: tile.id,
    label: tile.label,
    value: tile.value,
    captionLead: tile.captionLead,
    caption: tile.caption,
    tone: tile.tone,
  };
}

function miniFromTab(tile: TabKpiTile): GmMiniItem {
  return {
    id: tile.id,
    label: tile.label,
    value: tile.value,
    captionLead: tile.captionLead,
    caption: tile.caption,
    tone: tile.tone,
  };
}

/** Process KPI trends: tonnes, Pb recovery and the Zn float pH (Zn recovery leads the strip). */
export function cmetProcessMiniRow(state: SessionState): GmMiniItem[] {
  return metallurgistKpiRow(state)
    .filter((tile) => tile.id !== 'cmet-kpi-zn-rec')
    .map(miniFromMet);
}

/** Lab data: the two concentrate grades (Pb in Zn leads the strip; the next composite is the header fact). */
export function cmetLimsMiniRow(state: SessionState): GmMiniItem[] {
  return metallurgistLimsKpis(state)
    .filter((tile) => tile.id === 'cmet-lims-1' || tile.id === 'cmet-lims-2')
    .map(miniFromTab);
}

/** Active AI cases: resolved this week, FMEA references and resolution time. */
export function cmetCasesMiniRow(): GmMiniItem[] {
  return metallurgistCasesKpis().map(miniFromTab);
}

// ---- The Met-level issues ------------------------------------------------------------

function metLevelRows(rows: readonly IssueListRow[]): IssueListRow[] {
  return rows.filter((row) => MET_LEVEL_ISSUE_IDS.includes(row.id));
}

function perDayAmount(row: IssueListRow): number {
  if (row.impactUnitLabel.endsWith('one-off')) return 0;
  return Number(row.impactLabel.replace(/[^0-9.]/g, '')) || 0;
}

/** The Met-level issue carrying the largest active loss of its own (not a
 *  share within another issue): the one case the landing prices at stake. */
function atStakeRow(rows: readonly IssueListRow[]): IssueListRow {
  const candidates = metLevelRows(rows).filter((row) => !row.impactMuted);
  return [...candidates].sort((a, b) => perDayAmount(b) - perDayAmount(a))[0]!;
}

function caseFor(caseId: string | undefined, state: SessionState, advisory: AdvisorySessionState): Case | undefined {
  if (!caseId) return undefined;
  return casesWithSession(state, advisory).find((row) => row.id === caseId);
}

export type CmetAtStake = {
  label: string;
  figure: string;
  unit: string;
  comparisonLead: string;
  comparisonRest: string;
  caseId: string;
  href: string;
};

/** Adjudication 7: the single Met-level case's active loss, opening it in Advisory. */
export function cmetAtStake(state: SessionState, advisory: AdvisorySessionState, projectSlug: string): CmetAtStake {
  const row = atStakeRow(issueListRows('cmet', projectSlug, state));
  const caseItem = caseFor(row.caseId, state, advisory);
  const driverIssue = DRIVER_TREE_SEED.issues.find((issue) => issue.rank === row.rank);
  const vsDesign = driverIssue?.countedBasis?.kind === 'recovery-gap' ? ' vs design' : '';
  const figure = row.impactLabel.replace(/\/day$/, '');
  return {
    label: `At stake · ${caseItem?.code ?? row.title}`,
    figure,
    unit: '/day',
    comparisonLead: `${row.impType.toLowerCase()}${vsDesign}`,
    comparisonRest: ` · ${row.tier} · ${row.area}`,
    caseId: row.caseId ?? '',
    href: `/projects/${projectSlug}/advisory/case/${row.caseId ?? ''}`,
  };
}

// ---- Action records ----------------------------------------------------------------

const UPTAKE_IDS = new Set(UPTAKE_HISTORY_ACTIONS.map((row) => row.id));
const URGENCY_RANK: Record<RecommendedAction['urgency'], number> = { now: 0, today: 1, week: 2 };
const URGENCY_WORD: Record<RecommendedAction['urgency'], string> = { now: 'now', today: 'today', week: 'this week' };

/** The short noun a strip headline gives a decided step. A step with no entry
 *  reads as `step`. */
const ACTION_NOUN: Readonly<Record<string, string>> = {
  'ra-2': 'lime step',
};

/** The seat as the actor chip names it — the persona's short name, not the
 *  seat's switchboard label (`Metallurgist (Ext 2220)`). */
const ROLE_CHIP_LABEL: Partial<Record<PlantRoleId, string>> = {
  metallurgist: 'Control met',
  'senior-metallurgist': 'Senior met',
  maintenance: 'Maintenance',
  cro: 'Control room',
  operator: 'Operator',
  superintendent: 'Ops Supt',
  gm: 'GM',
  'ops-supervisor': 'Ops supervisor',
};

export function seatChipLabel(seatId: string): string {
  const seat = BUNKER_HILL_SEATS.find((row) => row.id === seatId);
  if (!seat) return seatId;
  return ROLE_CHIP_LABEL[seat.roleId] ?? seat.label;
}

/** The seat a case is *assigned* to, as the rail's actor chip names it. The
 *  assignment is a role id (`Case.assignedTo`), not a seat id, so it does not
 *  go through `seatChipLabel`: these are the plant's short names for the three
 *  seats Paul's Met-level issues sit with (Metallurgy, Maintenance, Control
 *  room). */
const ASSIGNED_SEAT_CHIP: Readonly<Partial<Record<PlantRoleId, string>>> = {
  metallurgist: 'Metallurgy',
  'senior-metallurgist': 'Metallurgy',
  maintenance: 'Maintenance',
  cro: 'Control room',
  operator: 'Control room',
  superintendent: 'Ops Supt',
  'ops-supervisor': 'Ops supervisor',
  gm: 'GM',
};

export function assignedSeatChip(assignedTo: string): string {
  return ASSIGNED_SEAT_CHIP[assignedTo as PlantRoleId] ?? assignedTo;
}

function clock(at: string, cursor: SeriesTime): string {
  return formatCaseClock(at, cursor);
}

export type RailRecordStatus = 'done' | 'approved' | 'rejected' | 'proposed';

export type RailRecord = {
  actionId: string;
  status: RailRecordStatus;
  statusLabel: string;
  text: string;
  /** The deciding seat as an actor chip; a proposed step has none. */
  seat?: string;
  detail: string;
};

export type RailShows = {
  stateLabel: string;
  tone: 'ok' | 'warn' | 'bad' | 'neutral';
  sentence: string;
};

export type RailGate = {
  caseId: string;
  actionIds: readonly string[];
  items: readonly string[];
  /** `Approved · 08 Jun 11:30 · Control met` once every step in the gate carries a decision. */
  approvedStamp?: string;
};

export type RailCase = {
  issueId: string;
  rank: number;
  caseId: string;
  code: string;
  /** The seat that holds the case (`Case.assignedTo`), as the rail's chip
   *  names it — the rail is Paul's Met-level issues, not Met-owned cases. */
  seat: string;
  title: string;
  moneyLead: string;
  moneyRest: string;
  records: readonly RailRecord[];
  shows?: RailShows;
  gate?: RailGate;
};

export type CmetRail = {
  count: string;
  cases: readonly RailCase[];
};

const STATUS_LABEL: Record<RailRecordStatus, string> = {
  done: 'Done',
  approved: 'Approved',
  rejected: 'Rejected',
  proposed: 'Proposed',
};

const SHOWS: Record<string, { label: string; tone: RailShows['tone'] } | undefined> = {
  'awaiting-action': { label: 'Awaiting action', tone: 'neutral' },
  watching: { label: 'Watching', tone: 'neutral' },
  improving: { label: 'Improving', tone: 'ok' },
  'no-change': { label: 'No change', tone: 'warn' },
  worsening: { label: 'Worsening', tone: 'bad' },
  resolved: { label: 'Resolved', tone: 'ok' },
};

function recordFor(action: RecommendedAction, cursor: SeriesTime): RailRecord {
  const status = action.status as RailRecordStatus;
  const decidedAt = action.decision?.at;
  let detail: string;
  if (status === 'proposed') {
    detail = `urgency: ${URGENCY_WORD[action.urgency]}`;
  } else if (status === 'rejected') {
    const reason = action.decision?.reason ? firstSentence(action.decision.reason).replace(/[,.]$/, '') : '';
    detail = [decidedAt ? clock(decidedAt, cursor) : '', reason].filter(Boolean).join(' · ');
  } else if (status === 'done') {
    detail = [
      decidedAt ? `decided ${clock(decidedAt, cursor)}` : '',
      action.implementedAt ? `in place ${clock(action.implementedAt, cursor)}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  } else {
    detail = decidedAt ? `decided ${clock(decidedAt, cursor)}` : '';
  }
  return {
    actionId: action.id,
    status,
    statusLabel: STATUS_LABEL[status],
    // The seed's parenthetical gap names (`(Zn recovery gap)`) are routing notes, not the step.
    text: action.text.replace(/\s*\([^)]*\)\s*$/, ''),
    seat: status === 'proposed' || !action.decision ? undefined : seatChipLabel(action.decision.seat),
    detail,
  };
}

function recordOrder(a: RecommendedAction, b: RecommendedAction): number {
  const aOpen = a.status === 'proposed';
  const bOpen = b.status === 'proposed';
  if (aOpen !== bOpen) return aOpen ? 1 : -1;
  if (aOpen) return URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] || a.id.localeCompare(b.id);
  return (a.decision?.at ?? '').localeCompare(b.decision?.at ?? '');
}

/** The seeded steps proposed for now or today on one case — the gate's items. */
function seededProposalsDue(caseId: string): RecommendedAction[] {
  return RECOMMENDED_ACTIONS.filter(
    (action) =>
      action.caseId === caseId &&
      !UPTAKE_IDS.has(action.id) &&
      action.status === 'proposed' &&
      action.urgency !== 'week',
  ).sort(recordOrder);
}

function gateItemLabel(text: string): string {
  return text.replace(/\s*\([^)]*\)\s*$/, '');
}

/**
 * `Met-level issues` (C's rail, the lead region of the Active AI cases view):
 * Paul's Met-level *issues* — not cases the Metallurgist owns — in the list's
 * engine rank, each stamped with the seat Advisory assigns it to and its
 * case's action record from Advisory, the Effectiveness engine's read of the
 * watched signal, and — on the first case with steps proposed for now or today
 * — the one approval gate the card holds.
 */
export function cmetRail(state: SessionState, advisory: AdvisorySessionState, projectSlug: string): CmetRail {
  const rows = issueListRows('cmet', projectSlug, state);
  const metRows = metLevelRows(rows);
  const merged = applyRecommendedActions(RECOMMENDED_ACTIONS, advisory);
  const diagnosis = diagnose(state, advisory);
  let gateTaken = false;
  const cases: RailCase[] = [];
  for (const row of metRows) {
    const caseItem = caseFor(row.caseId, state, advisory);
    if (!caseItem) continue;
    const watched = watchedActionForCase(caseItem, RECOMMENDED_ACTIONS, advisory);
    const due = gateTaken ? [] : seededProposalsDue(caseItem.id);
    const gateIds = new Set(due.map((action) => action.id));
    const caseActions = merged
      .filter((action) => action.caseId === caseItem.id)
      .filter((action) => !gateIds.has(action.id))
      .filter((action) => !UPTAKE_IDS.has(action.id) || action.id === watched?.id)
      .sort(recordOrder);
    let gate: RailGate | undefined;
    if (due.length > 0) {
      gateTaken = true;
      const decisions = due.map((action) => advisory.actionDecisions.get(action.id));
      const allDecided = decisions.every(Boolean);
      const last = decisions.filter(Boolean).sort((a, b) => a!.at.localeCompare(b!.at)).at(-1);
      gate = {
        caseId: caseItem.id,
        actionIds: due.map((action) => action.id),
        items: due.map((action) => gateItemLabel(action.text)),
        approvedStamp: allDecided && last ? `Approved · ${stampOf(last.at)} · ${seatChipLabel(last.seat)}` : undefined,
      };
    }
    const effectiveness = effectivenessFor(caseItem, state, advisory, diagnosis, RECOMMENDED_ACTIONS);
    const shows = SHOWS[effectiveness.state];
    const money = row.impactLabel;
    const within = row.contributorLabel?.match(/within #\d+/)?.[0];
    cases.push({
      issueId: row.id,
      rank: row.rank,
      caseId: caseItem.id,
      code: caseItem.code ?? caseItem.id,
      seat: assignedSeatChip(caseItem.assignedTo),
      title: row.title,
      moneyLead: money,
      moneyRest: ` ${row.impType.toLowerCase()}${within ? ` · ${within}` : ''} · ${row.tier}`,
      records: caseActions.map((action) => recordFor(action, state.cursor)),
      shows: shows ? { stateLabel: shows.label, tone: shows.tone, sentence: effectiveness.sentence } : undefined,
      gate,
    });
  }
  return { count: `${cases.length} of ${ISSUE_SPINE.length}`, cases };
}

// ---- The lead band ---------------------------------------------------------------------

function effectivenessLine(caseItem: Case, state: SessionState, advisory: AdvisorySessionState): string {
  const read = effectivenessFor(caseItem, state, advisory, diagnose(state, advisory), RECOMMENDED_ACTIONS);
  if (read.before && read.after && read.actionAt) {
    return `${read.label} ${read.before.value.toFixed(1)} → ${read.after.value.toFixed(1)} since ${clock(read.actionAt, state.cursor)}`;
  }
  return read.sentence;
}

/** The step the Effectiveness engine watches on the at-stake case, as the
 *  strip's action headline (`ZN-REC lime step done 08:00`). */
function actionHeadline(caseItem: Case, state: SessionState, advisory: AdvisorySessionState): string {
  const code = caseItem.code ?? caseItem.id;
  const watched = watchedActionForCase(caseItem, RECOMMENDED_ACTIONS, advisory);
  if (!watched) return code;
  const noun = ACTION_NOUN[watched.id] ?? 'step';
  const decisionAt = advisory.actionDecisions.get(watched.id)?.at ?? watched.decision?.at;
  if (watched.status === 'done' && watched.implementedAt) {
    return `${code} ${noun} done ${clock(watched.implementedAt, state.cursor)}`;
  }
  return decisionAt ? `${code} ${noun} approved ${clock(decisionAt, state.cursor)}` : code;
}

/** The strip: one cell per view (Zn recovery · Pb in Zn con · Met-level issues). */
export function cmetStripCells(
  state: SessionState,
  advisory: AdvisorySessionState,
  projectSlug: string,
): LandingStripCell<CmetTabId>[] {
  const zn = metallurgistKpiRow(state).find((tile) => tile.id === 'cmet-kpi-zn-rec')!;
  const pbInZn = metallurgistLimsKpis(state).find((tile) => tile.id === 'cmet-lims-3')!;
  const rows = issueListRows('cmet', projectSlug, state);
  const stake = atStakeRow(rows);
  const stakeCase = caseFor(stake.caseId, state, advisory);
  const actors: { actor: PipelineActor; label?: string }[] = [{ actor: 'Human', label: 'Control met' }];
  return [
    {
      id: 'kpi',
      name: 'Zn recovery · 24-h average',
      figure: zn.value,
      comparisonLead: zn.captionLead,
      comparisonRest: zn.caption,
      actor: 'Engine',
      provenance: zn.provenanceChip && zn.provenance ? { text: zn.provenanceChip, title: zn.provenance } : undefined,
    },
    {
      id: 'lims',
      name: pbInZn.label,
      figure: pbInZn.value,
      figureContext: pbInZn.valueContext,
      comparisonLead: pbInZn.captionLead ?? '',
      comparisonRest: pbInZn.caption,
      actor: 'System',
      actorLabel: 'LIMS',
    },
    {
      id: 'cases',
      name: 'Met-level issues',
      figure: String(metLevelRows(rows).length),
      figureContext: `of ${rows.length} open`,
      comparisonLead: stakeCase ? actionHeadline(stakeCase, state, advisory) : '',
      comparisonRest: stakeCase ? ` · ${effectivenessLine(stakeCase, state, advisory)}` : '',
      actor: 'Agent',
      moreActors: actors,
    },
  ];
}

// ---- The ranked list -------------------------------------------------------------------

export type CmetListRow = IssueListRow & {
  /** The money column's second line (`Active loss · Throughput arm · …`). */
  moneyCaption: string;
  /** False for the rows the rail above already carries with their action record. */
  showContext: boolean;
};

/** The six issues in the engine's rank; the rows the rail names drop their action text. */
export function cmetCaseList(state: SessionState, projectSlug: string): CmetListRow[] {
  return issueListRows('cmet', projectSlug, state).map((row) => ({
    ...row,
    moneyCaption: [
      row.impType,
      row.attributionLabel ?? row.contributorLabel ?? '',
      row.secondaryImpactLabel ?? '',
    ]
      .filter(Boolean)
      .join(' · '),
    showContext: !MET_LEVEL_ISSUE_IDS.includes(row.id),
  }));
}
